import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, CircularProgress, Dialog, Tooltip } from '@mui/material';
import { PharmacySelect, PharmacyMetric, PharmacyPagination } from './shared/PharmacyUI';
import { IOS as BaseIOS } from './shared/MenuLayout';
import api from '../services/api';
import './shared/IP01.css';
import { downloadIP01Pdf } from './shared/IP01Pdf';

// ── Tokens: mismos que Indicadores.jsx (copiados, no exportados de allá) ──────
const IOS = { ...BaseIOS, blue:'#548EE6', green:'#59B989', orange:'#E6A35D', red:'#DE777E', purple:'#9982D8', separator:'#E9EDF3', label:'#263348' };
const Sel = PharmacySelect;
const fmtP = (p) => (!p||p.length<6)?'':`${p.substring(4,6)}/${p.substring(0,4)}`;

const IND_CFG = {
  'SOBRESTOCK_14':{ bg:'#fff3f2', border:'#f4d2cf', text:'#ad514b', dot:'#DE777E', label:'Sobrestock > 14' },
  'SOBRESTOCK':   { bg:'#eff8f3', border:'#cce8d7', text:'#347759', dot:'#4DAE82' },
  'NORMOSTOCK':   { bg:'#eff8f3', border:'#cce8d7', text:'#347759', dot:'#83BF98' },
  'SUBSTOCK':     { bg:'#fff8ed', border:'#f0dfbd', text:'#9a7536', dot:'#E6A35D' },
  'DESABASTECIDO':{ bg:'#fff3f2', border:'#f4d2cf', text:'#ad514b', dot:'#DE777E' },
  'SIN ROTACION': { bg:'#f5f6f8', border:'#e1e5eb', text:'#768294', dot:'#A6B1C2' },
  'SIN CONSUMO':  { bg:'#f1f3f6', border:'#dde3eb', text:'#6b788b', dot:'#8290A6' },
};
const getInd = (v) => IND_CFG[typeof v==='string' ? v.toUpperCase() : ''] || { bg:IOS.bg, border:IOS.separator, text:IOS.label2, dot:IOS.label3 };

const CAMBIO_CFG = {
  'CORREGIDO':{ bg:'#eff8f3', border:'#cce8d7', text:'#347759', dot:'#4DAE82' },
  'SIGUE':    { bg:'#fff8ed', border:'#f0dfbd', text:'#9a7536', dot:'#E6A35D' },
  'EMPEORO':  { bg:'#fff3f2', border:'#f4d2cf', text:'#ad514b', dot:'#DE777E' },
  'NUEVO':    { bg:'#f0f5fc', border:'#d5e2f4', text:'#5277a8', dot:'#548EE6' },
  'OK':       { bg:'#f5f6f8', border:'#e1e5eb', text:'#768294', dot:'#A6B1C2' },
};
const CAMBIO_LBL = { CORREGIDO:'Corregido', SIGUE:'Sigue', EMPEORO:'Empeoró', NUEVO:'Nuevo', OK:'OK' };

// Pill con el mismo estilo que IndPill del módulo
const Pill = ({ valor, cfg, label }) => (
  <Box sx={{ display:'inline-flex', alignItems:'center', gap:'5px',
    px:'8px', py:'3px', minHeight:'24px', boxSizing:'border-box', borderRadius:'999px',
    bgcolor:cfg.bg, border:`1px solid ${cfg.border}` }}>
    <Box sx={{ width:5, height:5, flexShrink:0, borderRadius:'50%', bgcolor:cfg.dot }} />
    <Typography sx={{ fontSize:'0.69rem', lineHeight:1.35, fontWeight:600, color:cfg.text, whiteSpace:'nowrap' }}>
      {label || ({'SIN ROTACION':'Sin rotación','SIN CONSUMO':'Sin consumo','SOBRESTOCK':'Sobrestock','NORMOSTOCK':'Normostock','SUBSTOCK':'Substock','DESABASTECIDO':'Desabastecido'}[valor] || valor) || '—'}
    </Typography>
  </Box>
);
const IndPill = ({ valor, sm }) => valor ? <Pill valor={valor} cfg={getInd(valor)} label={getInd(valor).label} sm={sm} /> : <Typography sx={{ fontSize:'0.72rem', color:IOS.label3 }}>—</Typography>;
const CambioPill = ({ valor, sm }) => valor ? <Pill valor={valor} cfg={CAMBIO_CFG[valor]||CAMBIO_CFG.OK} label={CAMBIO_LBL[valor]||valor} sm={sm} /> : <Typography sx={{ fontSize:'0.72rem', color:IOS.label3 }}>—</Typography>;

// ── Helpers ───────────────────────────────────────────────────────────────────
const g = (r, ...keys) => { if(!r) return undefined; for(const k of keys) if(r[k]!==undefined) return r[k]; return undefined; };
const num = (v) => (v===null||v===undefined||v==='') ? null : Number(v);
const fmtPct = (v) => v===null||v===undefined ? '—' : `${Number(v).toFixed(2)}%`;
const fmtN = (v, d=0) => v===null||v===undefined ? '—' : Number(v).toLocaleString('es-PE', { minimumFractionDigits:d, maximumFractionDigits:d });
const varColor = (v) => v===null||v===undefined ? IOS.label3 : v>=50 ? IOS.green : v>=0 ? IOS.orange : IOS.red;
const mesActual = () => { const n=new Date(); return `${n.getFullYear()}${String(n.getMonth()+1).padStart(2,'0')}`; };
const listaPeriodos = (desde='202401') => {
  const out=[]; const fin=mesActual();
  let y=parseInt(desde.substring(0,4)), m=parseInt(desde.substring(4,6));
  while(true){ const p=`${y}${String(m).padStart(2,'0')}`; if(p>fin) break; out.push(p); m++; if(m>12){m=1;y++;} }
  return out.reverse();
};
const NIVEL_ORD = { UE:1, HOSPITAL:2, IPRESS:3 };
const NIVEL_LBL = { UE:'Unidad ejecutora', HOSPITAL:'Hospital', IPRESS:'IPRESS (postas)' };
const META = 50;

const cardSx = { bgcolor:IOS.surface, borderRadius:'16px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` };
const chipSx = { display:'inline-flex', alignItems:'center', px:1, py:0.3, borderRadius:'20px', bgcolor:IOS.bg, border:`1px solid ${IOS.separator}` };
const btnSx = (bg, fg='white') => ({ height:34, px:1.6, borderRadius:'10px', border:'none', bgcolor:bg, color:fg, cursor:'pointer',
  display:'flex', alignItems:'center', gap:0.8, transition:'all 0.15s', '&:hover':{ filter:'brightness(0.92)' }, '&:disabled':{ opacity:0.55, cursor:'wait' } });
const thSx = (align='left', extra={}) => ({ padding:'9px 10px', textAlign:align, fontSize:'0.72rem', fontWeight:700, color:IOS.label2,
  letterSpacing:'0.06em', textTransform:'uppercase', backgroundColor:IOS.surface, borderBottom:`1px solid ${IOS.separator}`, whiteSpace:'nowrap', ...extra });

const CloseBtn = ({ onClick, label }) => (
  <Box className="pharmacy-layout pharmacy-action" component="button" aria-label={label} onClick={onClick}
    sx={{ width:32, height:32, borderRadius:'50%', border:'none', flexShrink:0,
      bgcolor:'#E5E5EA', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
      transition:'all 0.15s', '&:hover':{ bgcolor:'#D1D1D6' } }}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
  </Box>
);

const Loader = ({ titulo, sub }) => (
  <Box sx={{ py:10, textAlign:'center' }}>
    <CircularProgress sx={{ color:IOS.blue, mb:2 }} />
    <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label, mb:0.5 }}>{titulo}</Typography>
    {sub && <Typography sx={{ color:IOS.label2, fontSize:'0.8rem', maxWidth:360, mx:'auto' }}>{sub}</Typography>}
  </Box>
);

const Vacio = ({ texto, color }) => (
  <Box className="pharmacy-card" sx={{ ...cardSx, boxShadow:'none', py:8, textAlign:'center' }}>
    <Typography sx={{ color:color||IOS.label2, fontSize:'0.88rem' }}>{texto}</Typography>
  </Box>
);

// ── Celdas de la tabla principal ─────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
const TabIP01 = ({ esTotal = false }) => {
  const [basal, setBasal]       = useState('202601');
  const [final, setFinal]       = useState(mesActual());
  const [filtros, setFiltros]   = useState({ basal:'202601', final:mesActual() }); // lo último buscado
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [error, setError]       = useState('');
  const [recalc, setRecalc]     = useState(false);
  const [confirm, setConfirm]   = useState(false);
  const reqId = useRef(0);

  // detalle
  const [grupoSel, setGrupoSel] = useState(null);
  const [det, setDet]           = useState([]);
  const [loadDet, setLoadDet]   = useState(false);
  const [errDet, setErrDet]     = useState('');
  const [fCambio, setFCambio]   = useState('TODOS');
  const [soloCuentan, setSoloCuentan] = useState(false);
  const [busq, setBusq]         = useState('');
  const [busqD, setBusqD]       = useState('');
  const [pagina, setPagina]     = useState(1);
  const [rpp, setRpp]           = useState(20);
  const detReqId = useRef(0);

  const opcBasal = useMemo(() => listaPeriodos('202401'), []);
  const opcFinal = useMemo(() => listaPeriodos(basal), [basal]);

  // ── Reporte ────────────────────────────────────────────────────────────────
  const cargar = useCallback(async (pb, pf, recalcular=false) => {
    const id = ++reqId.current;
    recalcular ? setRecalc(true) : setLoading(true);
    setError('');
    try {
      const r = recalcular
        ? await api.post('/farmacia/indicadoressis/ip01/recalcular', { periodoBasal:pb, periodoFinal:pf })
        : await api.get('/farmacia/indicadoressis/ip01/reporte', { params:{ periodoBasal:pb, periodoFinal:pf } });
      if (id !== reqId.current) return;
      if (r.data?.success) { setData(r.data.data || []); setFiltros({ basal:pb, final:pf }); }
      else { setData([]); setError(r.data?.message || 'No se pudo obtener el indicador.'); }
    } catch (e) {
      if (id !== reqId.current) return;
      setData([]);
      setError(e?.response?.status===403
        ? 'No tienes privilegio Total en Indicadores para recalcular.'
        : (e?.response?.data?.message || 'No se pudo obtener el indicador.'));
    } finally {
      if (id === reqId.current) { setLoading(false); setRecalc(false); }
    }
  }, []);

  useEffect(() => { cargar('202601', mesActual()); }, [cargar]);
  useEffect(() => { if (final < basal) setFinal(basal); }, [basal, final]);

  // ── Pivot (formato largo → una fila por grupo) ─────────────────────────────
  const { periodos, grupos, ue } = useMemo(() => {
    const per = new Set(); const map = new Map();
    for (const r of data) {
      const nivel = String(g(r,'nivel','NIVEL')||'').toUpperCase();
      const idGrupo = g(r,'idGrupo','ID_GRUPO');
      const periodo = String(g(r,'periodo','PERIODO')||'');
      if (!periodo) continue;
      per.add(periodo);
      const k = `${nivel}|${idGrupo}`;
      if (!map.has(k)) map.set(k, { nivel, idGrupo, nombre: g(r,'nombre','NOMBRE') || (nivel==='UE'?'Unidad ejecutora':`#${idGrupo}`), byPer:{} });
      map.get(k).byPer[periodo] = {
        num: num(g(r,'sobrestockPSR','SobrestockPSR')),
        den: num(g(r,'totalInventario','TotalInventario')),
        prop: num(g(r,'proporcion','Proporcion')),
        propBasal: num(g(r,'proporcionBasal','ProporcionBasal')),
        varPct: num(g(r,'varPct','VarPct')),
        cumple: num(g(r,'cumpleMeta','CumpleMeta')),
      };
    }
    const periodos = [...per].sort();
    const grupos = [...map.values()].sort((a,b) =>
      (NIVEL_ORD[a.nivel]||9)-(NIVEL_ORD[b.nivel]||9) || String(a.nombre).localeCompare(String(b.nombre),'es'));
    return { periodos, grupos, ue: grupos.find(x=>x.nivel==='UE') || null };
  }, [data]);

  const ultimo = periodos[periodos.length-1] || null;

  // KPIs UE
  const kUeBasal = ue?.byPer[filtros.basal]?.prop ?? null;
  const kUeUlt   = ultimo ? (ue?.byPer[ultimo]?.prop ?? null) : null;
  const kUeVar   = ultimo && ultimo!==filtros.basal ? (ue?.byPer[ultimo]?.varPct ?? null) : null;
  const kUeCumple= ultimo && ultimo!==filtros.basal ? (ue?.byPer[ultimo]?.cumple ?? null) : null;

  // ── Detalle ────────────────────────────────────────────────────────────────
  const abrirDetalle = useCallback(async (grupo) => {
    if (!ultimo) return;
    setGrupoSel(grupo); setDet([]); setErrDet(''); setFCambio('TODOS'); setSoloCuentan(false);
    setBusq(''); setBusqD(''); setPagina(1);
    const id = ++detReqId.current;
    setLoadDet(true);
    try {
      const r = await api.get('/farmacia/indicadoressis/ip01/detalle', { params:{
        periodo:ultimo, nivel:grupo.nivel, idGrupo:grupo.idGrupo, periodoBasal:filtros.basal, soloNumerador:false } });
      if (id !== detReqId.current) return;
      if (r.data?.success) setDet(r.data.data || []);
      else setErrDet(r.data?.message || 'No se pudo obtener el detalle.');
    } catch (e) {
      if (id !== detReqId.current) return;
      setErrDet(e?.response?.data?.message || 'No se pudo obtener el detalle.');
    } finally {
      if (id === detReqId.current) setLoadDet(false);
    }
  }, [ultimo, filtros.basal]);

  const cerrarDetalle = () => { detReqId.current++; setGrupoSel(null); setLoadDet(false); };

  useEffect(() => { const t = setTimeout(() => setBusqD(busq.trim().toLowerCase()), 300); return () => clearTimeout(t); }, [busq]);
  useEffect(() => { setPagina(1); }, [fCambio, soloCuentan, busqD]);
  useEffect(() => { if (soloCuentan && fCambio==='CORREGIDO') setFCambio('TODOS'); }, [soloCuentan, fCambio]);

  const detBase = useMemo(() => soloCuentan ? det.filter(d => g(d,'enNumerador','EN_NUMERADOR')) : det, [det, soloCuentan]);
  const conteo = useMemo(() => {
    const c = { TODOS:detBase.length };
    for (const d of detBase) { const k = g(d,'cambio','CAMBIO'); if (k) c[k] = (c[k]||0)+1; }
    return c;
  }, [detBase]);
  const detF = useMemo(() => detBase.filter(d => {
    if (fCambio!=='TODOS' && g(d,'cambio','CAMBIO')!==fCambio) return false;
    if (busqD) {
      const t = `${g(d,'codigoMed','CODIGO_MED')||''} ${g(d,'producto','PRODUCTO')||''}`.toLowerCase();
      if (!t.includes(busqD)) return false;
    }
    return true;
  }), [detBase, fCambio, busqD]);
  const detPag = detF.slice((pagina-1)*rpp, pagina*rpp);


  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box>
      {/* ── Cabecera del indicador ── */}
      <Box className="pharmacy-card" sx={{ ...cardSx, p:2.5, mb:2.5 }}>
        <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.8, flexWrap:'wrap', mb:1.2 }}>
          {[['IP01',IOS.blue],[`Meta: ${META}%`,IOS.green],['Sobrestock > 14 meses',IOS.orange],['PF · DM · PS',IOS.purple]].map(([t,c])=>(
            <Box key={t} sx={{ ...chipSx, bgcolor:`${c}12`, border:`1px solid ${c}30` }}>
              <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:c, whiteSpace:'nowrap' }}>{t}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontWeight:700, fontSize:'0.95rem', color:IOS.label, mb:1 }}>Variación porcentual del sobrestock</Typography>
        <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' }, gap:1 }}>
          {[['Proporción','(Sobrestock + PSR) / Total inventario × 100'],['Reducción','(Prop. basal − Prop. final) / Prop. basal × 100']].map(([l,f])=>(
            <Box key={l} sx={{ bgcolor:IOS.bg, borderRadius:'10px', px:1.5, py:1 }}>
              <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.06em' }}>{l}</Typography>
              <Typography sx={{ fontSize:'0.8rem', color:IOS.label, fontFamily:'monospace' }}>{f}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Filtros ── */}
      <Box className="pharmacy-layout pharmacy-toolbar pharmacy-card" sx={{ ...cardSx, p:2, mb:2.5,
        display:'flex', gap:1.5, flexWrap:'wrap', alignItems:'flex-end' }}>
        <Sel label="Periodo basal" value={basal} onChange={e=>setBasal(e.target.value)} w={{ xs:'calc(50% - 8px)', sm:150 }}>
          {opcBasal.map(p=><option key={p} value={p}>{fmtP(p)}</option>)}
        </Sel>
        <Sel label="Periodo final" value={final} onChange={e=>setFinal(e.target.value)} w={{ xs:'calc(50% - 8px)', sm:150 }}>
          {opcFinal.map(p=><option key={p} value={p}>{fmtP(p)}</option>)}
        </Sel>
        <Box className="pharmacy-layout pharmacy-action" component="button" onClick={()=>cargar(basal, final)} disabled={loading||recalc}
          sx={btnSx(IOS.blue)}>
          {loading ? <CircularProgress size={13} sx={{ color:'white' }} />
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>}
          <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'white' }}>Buscar</Typography>
        </Box>
        <Box component="button" className="pharmacy-layout pharmacy-action" disabled={loading||recalc||exporting||!grupos.length||!!error} sx={btnSx('#edf3fc','#416fa7')} onClick={async()=>{setExporting(true);setPdfError('');try{await downloadIP01Pdf({grupos,periodos,basal:filtros.basal});}catch(e){setPdfError('No se pudo generar el PDF. Intenta nuevamente.');}finally{setExporting(false);}}}>
          <Typography sx={{fontSize:'0.78rem',fontWeight:700}}>{exporting?'Generando PDF…':'Descargar PDF'}</Typography>
        </Box>
        {pdfError && <Typography role="alert" sx={{color:IOS.red,fontSize:'0.78rem'}}>{pdfError}</Typography>}
        {esTotal && (
          <Tooltip title="Vuelve a calcular todos los periodos del rango" placement="top">
            <Box className="pharmacy-layout pharmacy-action" component="button" onClick={()=>setConfirm(true)} disabled={loading||recalc}
              sx={{ ...btnSx('#FFFDF0','#8B6914'), border:'1.5px solid #FFE9A0', '&:hover':{ bgcolor:'#FFF8E6' } }}>
              {recalc ? <CircularProgress size={13} sx={{ color:'#8B6914' }} />
                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><path d="M21 3v5h-5"/></svg>}
              <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#8B6914' }}>Recalcular</Typography>
            </Box>
          </Tooltip>
        )}
        <Box className="pharmacy-layout" sx={{ ml:{ xs:0, sm:'auto' }, display:'flex', alignItems:'center', gap:0.8,
          px:1.5, py:0.6, bgcolor:IOS.bg, borderRadius:'20px', border:`1px solid ${IOS.separator}` }}>
          <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:IOS.green }} />
          <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color:IOS.label2 }}>
            Toda la UE · {fmtP(filtros.basal)} → {fmtP(ultimo || filtros.final)}
          </Typography>
        </Box>
      </Box>

      {/* ── Contenido ── */}
      {(loading || recalc)
        ? <Loader titulo={recalc ? `Recalculando ${periodos.length || 'los'} periodos…` : 'Calculando periodos…'}
            sub={recalc ? 'Se recalcula cada mes del rango; puede tardar unos minutos, no cierres esta pestaña.' : 'Calculando periodos, puede tardar unos segundos la primera vez.'} />
        : error
          ? <Vacio texto={error} color={IOS.red} />
          : grupos.length===0
            ? <Vacio texto="Sin datos para el rango seleccionado" />
            : <Box>
                {/* KPIs UE */}
                <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr 1fr', md:'repeat(4,1fr)' }, gap:1.5, mb:2.5 }}>
                  <PharmacyMetric label={`Proporción basal · ${fmtP(filtros.basal)}`} valor={fmtPct(kUeBasal)} color={IOS.purple}
                    sub={ue?.byPer[filtros.basal] ? `${fmtN(ue.byPer[filtros.basal].num)} de ${fmtN(ue.byPer[filtros.basal].den)} productos` : 'Sin dato basal'} />
                  <PharmacyMetric label={`Proporción · ${fmtP(ultimo)}`} valor={fmtPct(kUeUlt)} color={IOS.blue}
                    sub={ue?.byPer[ultimo] ? `${fmtN(ue.byPer[ultimo].num)} de ${fmtN(ue.byPer[ultimo].den)} productos` : '—'} />
                  <PharmacyMetric label="Reducción respecto al inicio" valor={fmtPct(kUeVar)} color={varColor(kUeVar)}
                    progress={kUeVar===null ? null : Math.max(0, Math.min(100, kUeVar/META*100))}
                    sub={kUeVar===null ? 'Sin periodo de comparación' : kUeVar<0 ? 'El sobrestock aumentó' : `Avance hacia la meta de ${META}%`} />
                  <PharmacyMetric label="Cumple meta" valor={kUeCumple===null ? '—' : kUeCumple===1 ? 'Sí' : 'No'}
                    color={kUeCumple===null ? IOS.label3 : kUeCumple===1 ? IOS.green : IOS.red}
                    sub={`Reducción ≥ ${META}% al ${fmtP(ultimo)}`} />
                </Box>

                {/* Tabla / cards */}
                <Box className="pharmacy-card" sx={{ ...cardSx, overflow:'hidden', mb:2.5 }}>
                  <Box sx={{ p:2.5, borderBottom:`1px solid ${IOS.separator}` }}>
                    <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>¿Dónde disminuyó el sobrestock?</Typography>
                    <Typography sx={{ fontSize:'0.7rem', color:IOS.label2, mt:0.2 }}>
                      {grupos.length} grupos · {periodos.length} periodos · clic en una fila para ver los productos de {fmtP(ultimo)}
                    </Typography>
                  </Box>

                  <div className="ip01-comparison-wrap" tabIndex={0} role="region" aria-label="Comparación de sobrestock inicial y final">
                    <table className="ip01-comparison">
                      <thead><tr><th>Establecimiento</th><th>Inicio <small>{fmtP(filtros.basal)}</small></th><th>Último corte <small>{fmtP(ultimo)}</small></th><th>Qué cambió</th><th>Meta de reducción · {META}%</th><th><span className="ip01-sr">Detalle</span></th></tr></thead>
                      <tbody>{grupos.map(gr=>{
                        const cb=gr.byPer[filtros.basal], cu=gr.byPer[ultimo];
                        const comparable=ultimo!==filtros.basal && cb?.prop>0 && cu?.prop!=null;
                        const v=comparable ? cu?.varPct : null;
                        const valid=v!=null && Number.isFinite(v);
                        const tone=!valid?'neutral':v<0?'worse':v>0?'better':'neutral';
                        const progress=valid?Math.max(0,Math.min(100,v/META*100)):0;
                        return <tr key={gr.nivel+'|'+gr.idGrupo} className={gr.nivel==='UE'?'ip01-ue':''}>
                          <td><strong>{gr.nombre}</strong><small>{NIVEL_LBL[gr.nivel]||gr.nivel}</small></td>
                          {[cb,cu].map((c,i)=><td key={i}><span className="ip01-proportion">{fmtPct(c?.prop)}</span><small>{c?fmtN(c.num)+' de '+fmtN(c.den)+' productos':'Sin datos'}</small><div className="ip01-proportion-track" aria-hidden="true"><i style={{width:Math.max(0,Math.min(100,c?.prop||0))+'%'}}/></div></td>)}
                          <td><span className={'ip01-change '+tone}>{!valid?'Sin comparación':v<0?'↑ Aumentó '+fmtPct(Math.abs(v)):v>0?'↓ Se redujo '+fmtPct(v):'Sin cambio'}</span><small>{!valid?(ultimo===filtros.basal?'Elige dos periodos distintos':cb?.prop===0?'La proporción inicial es cero':'Faltan datos para comparar'):'Respecto al inicio'}</small></td>
                          <td><span className={'ip01-target '+(valid&&cu?.cumple===1?'better':'neutral')}>{!valid?'No evaluable':cu?.cumple===1?'Meta alcanzada':v<0?'Requiere atención':v===0?'Sin avance':'En avance'}</span><div className="ip01-progress" aria-hidden="true"><i style={{width:progress+'%'}}/></div><small>{valid?'Reducción '+fmtPct(v)+' / Meta '+META+'%':'—'}</small></td>
                          <td><button type="button" className="ip01-detail-button" onClick={()=>abrirDetalle(gr)} aria-label={'Ver productos de '+gr.nombre}><svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="4" width="6" height="6" rx="1.5"/><rect x="14" y="4" width="6" height="6" rx="1.5"/><rect x="4" y="14" width="6" height="6" rx="1.5"/><rect x="14" y="14" width="6" height="6" rx="1.5"/></svg><span>Ver productos</span></button></td>
                        </tr>;
                      })}</tbody>
                    </table>
                  </div>
                  <p className="ip01-reading-note">Menor proporción significa menos sobrestock. La reducción compara porcentajes, no la cantidad de productos. UE, hospital e IPRESS son niveles de análisis: no se suman entre sí.</p>
                  <details className="ip01-history"><summary>Ver evolución mes a mes <span>{periodos.length} periodos</span></summary>
                  <div className="ip01-month-guide"><span><b>Porcentaje:</b> proporción de sobrestock + sin rotación.</span><span><b>↓ Redujo / ↑ Aumentó:</b> cambio respecto a {fmtP(filtros.basal)}, no al mes anterior.</span></div>
                  <div className="ip01-month-scroll" tabIndex={0} role="region" aria-label="Evolución mensual por establecimiento">
                    <table className="ip01-month-table">
                      <thead><tr className="ip01-month-heading"><th rowSpan={2} className="ip01-establishment-heading">Establecimiento</th>{periodos.map(p=><th colSpan={3} key={p} className={p===filtros.basal?'is-baseline':''}>{new Date(Number(p.slice(0,4)),Number(p.slice(4,6))-1,1).toLocaleDateString('es-PE',{month:'long',year:'numeric'})}<small>{p===filtros.basal?'Basal · punto de partida':p===ultimo?'Último corte':'Seguimiento mensual'}</small></th>)}</tr>
                      <tr className="ip01-month-subheading">{periodos.map(p=><React.Fragment key={p}><th className={'ip01-month-number '+(p===filtros.basal?'is-baseline':'')}>Sobrestock<small>+ sin rotación (PSR)</small></th><th className={'ip01-month-number '+(p===filtros.basal?'is-baseline':'')}>Inventario<small>Total de productos</small></th><th className={'ip01-month-proportion '+(p===filtros.basal?'is-baseline':'')}>Proporción<small>Cambio frente al basal</small></th></React.Fragment>)}</tr></thead>
                      <tbody>{['UE','HOSPITAL','IPRESS'].map(nv=>{
                        const list=grupos.filter(gr=>gr.nivel===nv);
                        if(!list.length)return null;
                        return <React.Fragment key={nv}><tr className="ip01-month-group"><td colSpan={periodos.length*3+1}><span>{NIVEL_LBL[nv]} · {list.length}</span></td></tr>
                        {list.map(gr=><tr key={gr.nivel+'|'+gr.idGrupo}>
                          <td><button type="button" onClick={()=>abrirDetalle(gr)} className="ip01-month-name" aria-label={'Ver productos de '+gr.nombre}>{gr.nombre}<small>Ver productos ↗</small></button></td>
                          {periodos.map(p=>{
                            const c=gr.byPer[p], isBase=p===filtros.basal;
                            const valid=!isBase && gr.byPer[filtros.basal]?.prop>0 && c?.prop!=null && c?.varPct!=null && Number.isFinite(c.varPct);
                            const v=valid?c.varPct:null;
                            return <React.Fragment key={p}><td className={'ip01-month-number '+(isBase?'is-baseline':'')}>{c?fmtN(c.num):'—'}</td><td className={'ip01-month-number '+(isBase?'is-baseline':'')}>{c?fmtN(c.den):'—'}</td><td className={'ip01-month-proportion '+(isBase?'is-baseline':'')}>
                              <strong className="ip01-month-value">{fmtPct(c?.prop)}</strong>
                              
                              <span className={'ip01-month-change '+(isBase?'baseline':!valid?'neutral':v<0?'worse':v>0?'better':'neutral')}>
                                {isBase?'Referencia':!valid?'Sin comparación':v<0?'↑ Aumentó '+fmtPct(Math.abs(v)):v>0?'↓ Redujo '+fmtPct(v):'Sin cambio'}
                              </span>
                            </td></React.Fragment>;
                          })}
                        </tr>)}</React.Fragment>;
                      })}</tbody>
                    </table>
                  </div>
                  </details>
                  {/* Leyenda semáforo */}
                  <Box className="pharmacy-layout" sx={{ px:2.5, py:1.5, borderTop:`1px solid ${IOS.separator}`, display:'flex', gap:2, flexWrap:'wrap' }}>
                    {[[IOS.green,`≥ ${META}% cumple meta`],[IOS.orange,`0% a ${META}% en avance`],[IOS.red,'< 0% empeoró']].map(([c,t])=>(
                      <Box key={t} className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:0.6 }}>
                        <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:c }} />
                        <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, fontWeight:600 }}>{t}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
      }

      {/* ── Notas ── */}
      <Box className="pharmacy-card" sx={{ ...cardSx, boxShadow:'none', p:2 }}>
        <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, mb:0.5 }}>
          <b>*</b> Sobrestock + PSR: productos con MSD &gt; 14 meses + productos sin rotación (stock &gt; 0 y consumo = 0 en 12 meses).
        </Typography>
        <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, mb:0.5 }}>
          <b>**</b> Total inventario: excluye productos sin consumo (stock = 0 y sin consumo en los últimos 4 meses).
        </Typography>
        <Typography sx={{ fontSize:'0.72rem', color:IOS.label2 }}>
          No considera el almacén de la UE; el hospital incluye sus farmacias y el almacén de farmacias.
        </Typography>
      </Box>

      {/* ── Confirmar recálculo ── */}
      <Dialog open={confirm} onClose={()=>setConfirm(false)} maxWidth="xs" fullWidth aria-labelledby="ip01-recalc-title"
        PaperProps={{ className:'pharmacy-page indicadores-page', sx:{ borderRadius:'24px', bgcolor:IOS.bg, boxShadow:'0 24px 60px rgba(0,0,0,0.18)', overflow:'hidden', m:{ xs:1, sm:2 } } }}>
        <Box className="pharmacy-layout" sx={{ bgcolor:IOS.surface, px:3, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:2 }}>
          <Box>
            <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase', mb:0.3 }}>IP01 SIS-FISSAL</Typography>
            <Typography component="h2" id="ip01-recalc-title" sx={{ fontSize:'0.95rem', fontWeight:700, color:IOS.label }}>Recalcular periodos</Typography>
          </Box>
          <CloseBtn onClick={()=>setConfirm(false)} label="Cerrar" />
        </Box>
        <Box sx={{ px:3, py:2.5 }}>
          <Typography sx={{ fontSize:'0.82rem', color:IOS.label, mb:2 }}>
            Se volverán a calcular todos los meses de {fmtP(basal)} a {fmtP(final)} para toda la unidad ejecutora. Puede tardar varios minutos.
          </Typography>
          <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'flex-end', gap:1 }}>
            <Box component="button" onClick={()=>setConfirm(false)} sx={btnSx('#E5E5EA', IOS.label)}>
              <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:IOS.label }}>Cancelar</Typography>
            </Box>
            <Box component="button" onClick={()=>{ setConfirm(false); cargar(basal, final, true); }} sx={btnSx(IOS.orange)}>
              <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'white' }}>Recalcular</Typography>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* ── Modal detalle ── */}
      <Dialog open={!!grupoSel} onClose={cerrarDetalle} maxWidth="lg" fullWidth aria-labelledby="ip01-det-title"
        PaperProps={{ className:'pharmacy-page indicadores-page', sx:{ borderRadius:'24px', bgcolor:IOS.bg, boxShadow:'0 24px 60px rgba(0,0,0,0.18)', overflow:'hidden', m:{ xs:1, sm:2 } } }}>
        <Box className="pharmacy-layout" sx={{ bgcolor:IOS.surface, px:3, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:2 }}>
          <Box sx={{ minWidth:0 }}>
            <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase', mb:0.3 }}>
              IP01 · {NIVEL_LBL[grupoSel?.nivel] || ''} · {fmtP(ultimo)} vs {fmtP(filtros.basal)}
            </Typography>
            <Typography component="h2" id="ip01-det-title" sx={{ fontSize:'0.95rem', fontWeight:700, color:IOS.label, lineHeight:1.2 }}>{grupoSel?.nombre}</Typography>
          </Box>
          <CloseBtn onClick={cerrarDetalle} label="Cerrar detalle" />
        </Box>

        <Box sx={{ overflow:'auto', maxHeight:'75vh', px:{ xs:1.5, sm:2.5 }, py:2,
          '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
          {loadDet
            ? <Box sx={{ py:8, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
            : errDet
              ? <Vacio texto={errDet} color={IOS.red} />
              : <Box className="pharmacy-card" sx={{ ...cardSx, overflow:'hidden' }}>
                  <Box sx={{ p:2, borderBottom:`1px solid ${IOS.separator}` }}>
                    {/* Filtro cambio */}
                    <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.8, flexWrap:'wrap', mb:1.5, alignItems:'center' }}>
                      {['TODOS','CORREGIDO','SIGUE','EMPEORO','NUEVO'].map(key=>{
                        const cfg = key==='TODOS' ? null : CAMBIO_CFG[key];
                        const active = fCambio===key;
                        const dis = key==='CORREGIDO' && soloCuentan;
                        return (
                          <Tooltip key={key} title={dis ? 'Los corregidos ya no cuentan en el indicador' : ''} placement="top">
                            <Box onClick={()=>{ if(!dis) setFCambio(key); }}
                              sx={{ px:1.2, py:0.3, borderRadius:'20px', cursor:dis?'not-allowed':'pointer', opacity:dis?0.45:1,
                                border:`1px solid ${active?(cfg?.border||IOS.blue):IOS.separator}`,
                                bgcolor:active?(cfg?.bg||`${IOS.blue}12`):'transparent', transition:'all 0.15s' }}>
                              <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:active?(cfg?.text||IOS.blue):IOS.label2, whiteSpace:'nowrap' }}>
                                {key==='TODOS' ? 'Todos' : CAMBIO_LBL[key]} · {conteo[key]||0}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {/* Toggle solo los que cuentan */}
                      <Box onClick={()=>setSoloCuentan(v=>!v)} role="switch" aria-label="Solo sobrestock y sin rotación" aria-checked={soloCuentan} tabIndex={0} onKeyDown={e=>{if(e.key===' '||e.key==='Enter'){e.preventDefault();setSoloCuentan(v=>!v);}}}
                        sx={{ ml:{ xs:0, sm:'auto' }, display:'flex', alignItems:'center', gap:0.8, cursor:'pointer', userSelect:'none' }}>
                        <Box sx={{ width:34, height:20, borderRadius:'20px', bgcolor:soloCuentan?IOS.blue:'#E5E5EA', position:'relative', transition:'all 0.2s' }}>
                          <Box sx={{ width:16, height:16, borderRadius:'50%', bgcolor:'white', position:'absolute', top:2, left:soloCuentan?16:2,
                            boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'all 0.2s' }} />
                        </Box>
                        <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:soloCuentan?IOS.label:IOS.label2 }}>Solo sobrestock y sin rotación</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{fontSize:'0.7rem',color:IOS.label2,mb:1.5}}>Al activarlo, muestra solo los productos que suman al numerador del IP01 en el corte actual.</Typography>
                    {/* Búsqueda */}
                    <Box sx={{ position:'relative', maxWidth:{ xs:'100%', sm:400 } }}>
                      <Box sx={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:IOS.label2, pointerEvents:'none' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                      </Box>
                      <input type="text" placeholder="Buscar por código o producto..." value={busq}
                        onChange={e=>setBusq(e.target.value)}
                        style={{ width:'100%', height:34, paddingLeft:30, paddingRight:12,
                          border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.78rem',
                          color:IOS.label, backgroundColor:IOS.surface, outline:'none',
                          boxSizing:'border-box', fontFamily:'inherit' }}
                        onFocus={e=>e.target.style.borderColor=IOS.blue}
                        onBlur={e=>e.target.style.borderColor=IOS.separator} />
                    </Box>
                  </Box>

                  {/* Móvil — cards */}
                  <Box sx={{ display:{ xs:'block', md:'none' } }}>
                    {detPag.length===0
                      ? <Box sx={{ p:4, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Sin productos</Typography></Box>
                      : detPag.map((d,idx)=>(
                          <Box key={(g(d,'codigoMed','CODIGO_MED')||'')+idx} sx={{ p:2, borderBottom:`1px solid ${IOS.separator}` }}>
                            <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:0.8 }}>
                              <Box sx={{ minWidth:0 }}>
                                <Typography sx={{ fontWeight:700, fontSize:'0.8rem', color:IOS.label, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g(d,'producto','PRODUCTO')}</Typography>
                                <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{g(d,'codigoMed','CODIGO_MED')} · {g(d,'tipo','TIPO')}</Typography>
                              </Box>
                              <CambioPill valor={g(d,'cambio','CAMBIO')} sm />
                            </Box>
                            <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:0.8, mb:0.8 }}>
                              {[['Stock',fmtN(g(d,'stock','STOCK'))],['CPMA',fmtN(g(d,'cpma','CPMA'),2)],['MSD',fmtN(g(d,'msd','MSD'),2)],['MSD bas.',fmtN(num(g(d,'msdBasal','MSD_BASAL')),2)]].map(([l,v],j)=>(
                                <Box key={j} sx={{ bgcolor:IOS.bg, borderRadius:'8px', px:0.5, py:0.5, textAlign:'center' }}>
                                  <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>{l}</Typography>
                                  <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:IOS.label }}>{v}</Typography>
                                </Box>
                              ))}
                            </Box>
                            <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.8, alignItems:'center', flexWrap:'wrap' }}>
                              <IndPill valor={g(d,'condicion','CONDICION')} sm />
                              <Typography sx={{ fontSize:'0.72rem', color:IOS.label3 }}>basal</Typography>
                              <IndPill valor={g(d,'condicionBasal','CONDICION_BASAL')} sm />
                            </Box>
                          </Box>
                        ))
                    }
                  </Box>

                  {/* Desktop — tabla */}
                  <Box className="pharmacy-scroll" tabIndex={0} role="region" aria-label="Detalle de productos, desplazamiento horizontal"
                    sx={{ display:{ xs:'none', md:'block' }, overflowX:'auto', maxHeight:460,
                      '&::-webkit-scrollbar':{ width:4, height:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                    <table style={{ width:'100%', minWidth:900, borderCollapse:'collapse', fontSize:'0.75rem' }}>
                      <thead style={{ position:'sticky', top:0, zIndex:2 }}>
                        <tr>
                          {[['Código','left'],['Producto','left'],['Tipo','center'],['Stock','right'],['CPMA','right'],['MSD','right'],['Condición','center'],['Cond. basal','center'],['MSD basal','right'],['Cambio','center']].map(([h,a])=>(
                            <th key={h} style={thSx(a)}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detPag.length===0
                          ? <tr><td colSpan={10} style={{ padding:'40px', textAlign:'center', color:IOS.label2 }}>Sin productos</td></tr>
                          : detPag.map((d,idx)=>(
                              <tr key={(g(d,'codigoMed','CODIGO_MED')||'')+idx} style={{ borderBottom:`1px solid ${IOS.separator}`, backgroundColor:IOS.surface }}>
                                <td style={{ padding:'9px 10px', fontSize:'0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{g(d,'codigoMed','CODIGO_MED')}</td>
                                <td style={{ padding:'9px 10px', fontSize:'0.76rem', color:IOS.label, maxWidth:280 }}>
                                  <Tooltip title={g(d,'producto','PRODUCTO')||''} placement="top">
                                    <span style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:280 }}>{g(d,'producto','PRODUCTO')}</span>
                                  </Tooltip>
                                </td>
                                <td style={{ padding:'9px 10px', textAlign:'center', color:IOS.label2 }}>{g(d,'tipo','TIPO')}</td>
                                <td style={{ padding:'9px 10px', textAlign:'right', color:IOS.label }}>{fmtN(g(d,'stock','STOCK'))}</td>
                                <td style={{ padding:'9px 10px', textAlign:'right', color:IOS.label }}>{fmtN(g(d,'cpma','CPMA'),2)}</td>
                                <td style={{ padding:'9px 10px', textAlign:'right', color:IOS.label, fontWeight:700 }}>{fmtN(g(d,'msd','MSD'),2)}</td>
                                <td style={{ padding:'9px 10px', textAlign:'center' }}><IndPill valor={g(d,'condicion','CONDICION')} sm /></td>
                                <td style={{ padding:'9px 10px', textAlign:'center' }}><IndPill valor={g(d,'condicionBasal','CONDICION_BASAL')} sm /></td>
                                <td style={{ padding:'9px 10px', textAlign:'right', color:IOS.label2 }}>{fmtN(num(g(d,'msdBasal','MSD_BASAL')),2)}</td>
                                <td style={{ padding:'9px 10px', textAlign:'center' }}><CambioPill valor={g(d,'cambio','CAMBIO')} sm /></td>
                              </tr>
                            ))
                        }
                      </tbody>
                    </table>
                  </Box>

                  {detF.length > 0 && (
                    <Box sx={{ borderTop:`1px solid ${IOS.separator}` }}>
                      <PharmacyPagination total={detF.length} pagina={pagina} setPagina={setPagina} rpp={rpp} setRpp={setRpp} />
                    </Box>
                  )}
                </Box>
          }
        </Box>
      </Dialog>
    </Box>
  );
};

export default TabIP01;
